import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../CartContext";
import { fmtINR } from "../api";
import { X, ShoppingBag, Gift } from "lucide-react";

/**
 * Modal that previews everything inside a Kit before adding to cart.
 * Triggered when a customer clicks "Add to Cart" on a Kit-category product.
 */
export default function KitDetailModal({ kit, onClose }) {
    const { add } = useCart();
    const items = kit.kit_items?.length ? kit.kit_items : (kit.ingredients || []);
    const onAdd = () => {
        add(kit, 1);
        onClose();
    };
    return (
        <div className="drawer-backdrop" onClick={onClose} data-testid="kit-detail-modal-backdrop">
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white max-w-[720px] mx-auto my-10 rounded-2xl overflow-hidden shadow-xl"
                data-testid={`kit-detail-modal-${kit.id}`}
            >
                <div className="grid md:grid-cols-2">
                    <div className="relative aspect-square bg-gradient-to-br from-[#efe9fb] to-[#dbeedd]">
                        {kit.images?.[0] ? (
                            <img src={kit.images[0]} alt={kit.name} className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                            <div className="absolute inset-0 grid place-items-center text-[88px]">{kit.emoji || "🎁"}</div>
                        )}
                        <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[var(--taupe)]">
                            <Gift size={11} /> Curated Kit
                        </div>
                    </div>
                    <div className="p-6 lg:p-8 flex flex-col">
                        <button onClick={onClose} className="self-end text-[var(--body)] hover:text-[var(--ink)] -mt-2 -mr-2 p-1" data-testid="close-kit-modal">
                            <X size={18} />
                        </button>
                        <div className="overline mb-1.5">{kit.category}</div>
                        <h3 className="font-serif text-[22px] leading-snug text-[var(--ink)] mb-2">{kit.name}</h3>
                        <div className="text-[12.5px] text-[var(--body)] mb-3">
                            {kit.size || "Set"} · <span className="text-[var(--ink)] font-medium">{fmtINR(kit.price)}</span>
                            {kit.original_price && kit.original_price > kit.price && (
                                <span className="text-[var(--body)] line-through ml-2">{fmtINR(kit.original_price)}</span>
                            )}
                        </div>
                        <p className="text-[13px] text-[var(--body)] leading-relaxed mb-4">
                            {kit.short_desc || kit.description}
                        </p>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--taupe)] mb-3 flex items-center gap-1.5">
                            What's inside
                        </div>
                        <div className="flex-1 overflow-y-auto pr-1 -mr-1 max-h-[260px] mb-4">
                            {items.length === 0 ? (
                                <p className="text-[13px] text-[var(--body)]">Kit contents will appear here.</p>
                            ) : (
                                <ul className="space-y-3">
                                    {items.map((it, i) => (
                                        <li key={it.name || `item-${i}`} className="border-b border-[var(--line-soft)] pb-2.5" data-testid={`kit-item-${i}`}>
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="font-serif text-[15px] text-[var(--ink)]">{it.name}</span>
                                                {(it.quantity || it.percentage) && (
                                                    <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--taupe)] shrink-0">
                                                        {it.quantity || it.percentage}
                                                    </span>
                                                )}
                                            </div>
                                            {(it.description || it.benefit) && (
                                                <p className="text-[12px] text-[var(--body)] leading-relaxed mt-1">{it.description || it.benefit}</p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="flex gap-2 mt-auto">
                            <Link to={`/product/${kit.id}`} className="btn btn-outline btn-sm flex-1" onClick={onClose}>
                                Full details
                            </Link>
                            <button onClick={onAdd} className="btn btn-primary btn-sm flex-1" data-testid={`kit-modal-add-${kit.id}`}>
                                <ShoppingBag size={13} /> Add Kit · {fmtINR(kit.price)}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
